/**
 * i18n Service - Handle internationalization and UI string translations
 */

import logger from '../core/logger.js';

/**
 * Type for translation strings record
 */
type TranslationStrings = Record<string, string>;

/**
 * Type for placeholder replacements
 */
type Replacements = Record<string, string | number>;

/**
 * Supported language codes
 */
type LanguageCode = 'en' | 'tr' | 'de' | string;

// Internal state
let uiStrings: TranslationStrings = {};
let currentLanguage: LanguageCode = 'en';

/**
 * Load UI strings for a specific language
 * @param language - Language code (e.g., 'en', 'tr', 'de')
 * @param callback - Optional callback to execute after loading
 */
function loadUIStrings(language: LanguageCode, callback?: () => void): void {
  fetch(`data/i18n/${language}.json`, {
    cache: 'no-store'  // Prevent browser from caching i18n files
  })
    .then(res => {
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      return res.json();
    })
    .then((data: TranslationStrings) => {
      uiStrings = data;
      currentLanguage = language;
      logger.info('i18n: Loaded strings', { language, count: Object.keys(data).length });
      if (typeof callback === 'function') callback();
    })
    .catch(err => {
      logger.error('i18n: Failed to load', { language, error: err });
      // fallback to English if not found
      if (language !== 'en') {
        loadUIStrings('en', callback);
      } else {
        // If English also fails, invoke callback to prevent app from hanging
        if (typeof callback === 'function') callback();
      }
    });
}

/**
 * Format i18n strings with placeholders
 * @param key - Translation key
 * @param replacements - Object with placeholder replacements
 * @returns Formatted string
 */
function formatUIString(key: string, replacements: Replacements = {}): string {
  let str = uiStrings[key] || key;
  Object.keys(replacements).forEach(placeholder => {
    str = str.replace(new RegExp(`\\{${placeholder}\\}`, 'g'), String(replacements[placeholder]));
  });
  return str;
}

/**
 * Get a translated string by key
 * @param key - Translation key
 * @returns Translated string or key if not found
 */
function getString(key: string): string {
  return uiStrings[key] || key;
}

/**
 * Get the current UI language
 * @returns Current language code
 */
function getCurrentLanguage(): LanguageCode {
  return currentLanguage;
}

/**
 * Set the current language (internal state only, doesn't load)
 * @param language - Language code
 */
function setLanguage(language: LanguageCode): void {
  currentLanguage = language;
}

/**
 * Get all UI strings
 * @returns All loaded UI strings (shallow copy)
 */
function getAllStrings(): TranslationStrings {
  return { ...uiStrings };
}

// Export i18n service interface
export const i18nService = {
  loadUIStrings,
  formatUIString,
  getString,
  getCurrentLanguage,
  setLanguage,
  getAllStrings
};

// Export individual functions for backward compatibility
export {
  loadUIStrings,
  formatUIString,
  getString,
  getCurrentLanguage,
  setLanguage,
  getAllStrings
};
